import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { traderService, type Trader, type TraderFeatures } from '../services/traderService';
import craftsmanService, { type Craftsman } from '../services/craftsmanService';
import { supabase, supabaseAdmin } from '../config/supabase';
import { Skeleton } from '../components/ui/skeleton';
import { CheckCircle, MapPin, Phone, Mail, Globe, Pencil, Building2, Loader2, UserPlus } from 'lucide-react';
import SocialLinksCard from '../components/dashboard/SocialLinksCard';
import FeatureShowcase, { type FeatureShowcaseLayout } from '../components/FeatureShowcase';
import { compressImage, AVATAR_PRESET, GALLERY_PRESET, formatFileSize, validateImageFile } from '../utils/imageCompression';

// Generic user dashboard shared by all non-admin users for now.
// This keeps the post-login experience consistent while we evolve
// role-specific dashboards (trader, craftie, hospitality) later.

type HospitalityCategory = {
  id: number;
  name: string;
  category: string;
};

type HospitalityBusiness = {
  id: number;
  user_id: string;
  store_name: string;
  owner_name: string;
  email: string;
  phone_number: string;
  location: string;
  description: string;
  slug?: string;
  avatar_url?: string;
  website?: string;
  verified?: boolean;
  mockup?: boolean;
  hospitality_categories?: HospitalityCategory;
  show_map?: boolean;
};

type ProfessionalCategory = {
  id: number;
  name: string;
  category: string;
};

type ProfessionalBusiness = {
  id: number;
  user_id: string;
  store_name: string;
  owner_name: string;
  email: string;
  phone_number: string;
  location: string;
  description: string;
  slug?: string;
  avatar_url?: string;
  website?: string;
  verified?: boolean;
  mockup?: boolean;
  professionals_categories?: ProfessionalCategory;
  show_map?: boolean;
};

type BusinessFormState = {
  store_name: string;
  phone_number: string;
  email: string;
  website: string;
  location: string;
  description: string;
};

// Shared Type A trade categories for traders (TYPE A: Trades & Services)
const TRADER_TYPE_A_CATEGORIES: string[] = [
  'Building & Construction',
  'Interiors & Renovation',
  'Plumbing & Heating',
  'Home Maintenance',
  'Garden & Outdoors',
  'Cleaning & Logistics',
  'Motors',
  'Health & Lifestyle',
  'Technology & IT',
  'Creative & Media',
];

const UserDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businessType, setBusinessType] = useState<'trader' | 'craftie' | 'hospitality' | 'professional' | null>(null);
  const [trader, setTrader] = useState<Trader | null>(null);
  const [craftie, setCraftie] = useState<Craftsman | null>(null);
  const [hospitality, setHospitality] = useState<HospitalityBusiness | null>(null);
  const [professional, setProfessional] = useState<ProfessionalBusiness | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState<BusinessFormState | null>(null);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [professionOptions, setProfessionOptions] = useState<Array<{ id: number; name: string; category?: string | null }>>([]);
  const [selectedProfessionId, setSelectedProfessionId] = useState<number | null>(null);
  const [professionLoading, setProfessionLoading] = useState(false);
  const [professionError, setProfessionError] = useState<string | null>(null);
  const [selectedTypeACategory, setSelectedTypeACategory] = useState<string>('');
  const [secondaryProfessionId, setSecondaryProfessionId] = useState<number | null>(null);
  const [traderFeatures, setTraderFeatures] = useState<TraderFeatures | null>(null);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [featuresError, setFeaturesError] = useState<string | null>(null);
  const [editingFeature, setEditingFeature] = useState<{
    key: 'feature1' | 'feature2' | 'feature3';
    title: string;
    description: string;
    enabled: boolean;
    image: string;
  } | null>(null);
  const [featureImageFile, setFeatureImageFile] = useState<File | null>(null);
  const [localFeatureToggles, setLocalFeatureToggles] = useState<{
    feature1_enabled: boolean;
    feature2_enabled: boolean;
    feature3_enabled: boolean;
  }>({
    feature1_enabled: false,
    feature2_enabled: false,
    feature3_enabled: false,
  });
  const [featureSectionTitle, setFeatureSectionTitle] = useState('Featured Offerings');
  const [featureLayout, setFeatureLayout] = useState<FeatureShowcaseLayout>('rows');
  const [featureMetaSaving, setFeatureMetaSaving] = useState(false);
  const [featureMetaError, setFeatureMetaError] = useState<string | null>(null);
  // Local state for the delete account confirmation dialog.
  // This controls visibility of the popup and whether the user also wants to remove business data.
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [removeBusinessInfo, setRemoveBusinessInfo] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // State for claimable business matching user's email
  const [claimableBusiness, setClaimableBusiness] = useState<{
    id: number | string;
    store_name: string;
    location: string;
    avatar_url?: string;
    table: 'traders' | 'crafties' | 'hospitality' | 'professionals';
  } | null>(null);
  const [claimingBusiness, setClaimingBusiness] = useState(false);

  useEffect(() => {
    const fetchBusiness = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Try trader profile first
        const traderData = await traderService.getTraderByUserId(user.id);
        if (traderData) {
          setTrader(traderData);
          setBusinessType('trader');
          return;
        }

        // Then try craftie profile
        const craftieData = await craftsmanService.getCraftsmanByUserId(user.id);
        if (craftieData) {
          setCraftie(craftieData);
          setBusinessType('craftie');
          return;
        }

        // Finally try hospitality business
        const { data: hospitalityData, error: hospitalityError } = await supabase
          .from('hospitality')
          .select(`
            *,
            hospitality_categories (
              id,
              name,
              category
            )
          `)
          .eq('user_id', user.id)
          .single();

        if (hospitalityError) {
          // If no row found, just treat as no hospitality business
          console.log('Hospitality lookup for dashboard:', hospitalityError.message);
        }

        if (hospitalityData) {
          setHospitality(hospitalityData as HospitalityBusiness);
          setBusinessType('hospitality');
          return;
        }

        const { data: professionalData, error: professionalError } = await supabase
          .from('professionals')
          .select(`
            *,
            professionals_categories (
              id,
              name,
              category
            )
          `)
          .eq('user_id', user.id)
          .single();

        if (professionalError) {
          console.log('Professional lookup for dashboard:', professionalError.message);
        }

        if (professionalData) {
          setProfessional(professionalData as ProfessionalBusiness);
          setBusinessType('professional');
        }
      } catch (err) {
        console.error('Error loading user dashboard business:', err);
        setError('Failed to load your business details');
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();
  }, [user]);

  // Search for unclaimed businesses matching user's email when no business is found
  useEffect(() => {
    const searchClaimableBusiness = async () => {
      // Only search if user exists, has email, loading is done, and no business found
      if (!user?.email || loading || businessType) {
        return;
      }

      try {
        // Search across all business tables for unclaimed businesses matching user's email
        // Unclaimed = mockup=true AND user_id is null AND email matches
        
        // Check traders table
        const { data: traderMatch } = await supabase
          .from('traders')
          .select('id, store_name, location, avatar_url, email')
          .eq('email', user.email)
          .eq('mockup', true)
          .is('user_id', null)
          .single();

        if (traderMatch) {
          setClaimableBusiness({
            id: traderMatch.id,
            store_name: traderMatch.store_name,
            location: traderMatch.location,
            avatar_url: traderMatch.avatar_url,
            table: 'traders'
          });
          return;
        }

        // Check crafties table
        const { data: craftieMatch } = await supabase
          .from('crafties')
          .select('id, store_name, location, avatar_url, email')
          .eq('email', user.email)
          .eq('mockup', true)
          .is('user_id', null)
          .single();

        if (craftieMatch) {
          setClaimableBusiness({
            id: craftieMatch.id,
            store_name: craftieMatch.store_name,
            location: craftieMatch.location,
            avatar_url: craftieMatch.avatar_url,
            table: 'crafties'
          });
          return;
        }

        // Check hospitality table
        const { data: hospitalityMatch } = await supabase
          .from('hospitality')
          .select('id, store_name, location, avatar_url, email')
          .eq('email', user.email)
          .eq('mockup', true)
          .is('user_id', null)
          .single();

        if (hospitalityMatch) {
          setClaimableBusiness({
            id: hospitalityMatch.id,
            store_name: hospitalityMatch.store_name,
            location: hospitalityMatch.location,
            avatar_url: hospitalityMatch.avatar_url,
            table: 'hospitality'
          });
          return;
        }

        // Check professionals table
        const { data: professionalMatch } = await supabase
          .from('professionals')
          .select('id, store_name, location, avatar_url, email')
          .eq('email', user.email)
          .eq('mockup', true)
          .is('user_id', null)
          .single();

        if (professionalMatch) {
          setClaimableBusiness({
            id: professionalMatch.id,
            store_name: professionalMatch.store_name,
            location: professionalMatch.location,
            avatar_url: professionalMatch.avatar_url,
            table: 'professionals'
          });
        }
      } catch (err) {
        // Silently fail - not finding a claimable business is not an error
        console.log('No claimable business found for email:', user.email);
      }
    };

    searchClaimableBusiness();
  }, [user?.email, loading, businessType]);

  // Function to claim a business - assigns user_id to the unclaimed business
  const handleClaimBusiness = async () => {
    if (!claimableBusiness || !user) return;

    setClaimingBusiness(true);
    setError(null);

    try {
      console.log('Claiming business:', claimableBusiness.table, 'id:', claimableBusiness.id, 'user:', user.id);
      
      // Update the business record to assign the current user
      // Use supabaseAdmin to bypass RLS policies that may block the update
      const { data: updateData, error: updateError } = await supabaseAdmin
        .from(claimableBusiness.table)
        .update({ 
          user_id: user.id,
          mockup: false // Mark as no longer a mockup since it's now claimed
        })
        .eq('id', claimableBusiness.id)
        .select();

      console.log('Update result:', updateData, 'error:', updateError);

      if (updateError) {
        throw updateError;
      }
      
      if (!updateData || updateData.length === 0) {
        throw new Error('Update did not affect any rows. The business may have already been claimed.');
      }

      // Fetch the claimed business and update state directly
      // This avoids issues with page reload not properly refreshing state
      if (claimableBusiness.table === 'traders') {
        const traderData = await traderService.getTraderByUserId(user.id);
        if (traderData) {
          setTrader(traderData);
          setBusinessType('trader');
          setClaimableBusiness(null); // Clear the claimable business
        }
      } else if (claimableBusiness.table === 'crafties') {
        const craftieData = await craftsmanService.getCraftsmanByUserId(user.id);
        if (craftieData) {
          setCraftie(craftieData);
          setBusinessType('craftie');
          setClaimableBusiness(null);
        }
      } else if (claimableBusiness.table === 'hospitality') {
        const { data: hospitalityData } = await supabase
          .from('hospitality')
          .select(`*, hospitality_categories (id, name, category)`)
          .eq('user_id', user.id)
          .single();
        if (hospitalityData) {
          setHospitality(hospitalityData as HospitalityBusiness);
          setBusinessType('hospitality');
          setClaimableBusiness(null);
        }
      } else if (claimableBusiness.table === 'professionals') {
        const { data: professionalData } = await supabase
          .from('professionals')
          .select(`*, professionals_categories (id, name, category)`)
          .eq('user_id', user.id)
          .single();
        if (professionalData) {
          setProfessional(professionalData as ProfessionalBusiness);
          setBusinessType('professional');
          setClaimableBusiness(null);
        }
      }
    } catch (err) {
      console.error('Error claiming business:', err);
      setError('Failed to claim business. Please try again.');
    } finally {
      setClaimingBusiness(false);
    }
  };

  // Load profession/category options based on current business type
  useEffect(() => {
    const loadProfessions = async () => {
      if (!businessType) return;

      try {
        setProfessionLoading(true);
        setProfessionError(null);

        if (businessType === 'trader' && trader) {
          const { data, error: profError } = await supabase
            .from('professions')
            .select('id, name, category')
            .order('name');

          if (profError) throw profError;

          const options = (data || []) as Array<{ id: number; name: string; category?: string | null }>;
          setProfessionOptions(options);

          const currentId = trader.profession
            ? (trader.profession as any).id
            : trader.profession_id
            ? Number(trader.profession_id)
            : undefined;

          if (currentId) {
            setSelectedProfessionId(currentId);
            const currentProf = options.find((opt) => opt.id === currentId);
            if (currentProf?.category && !selectedTypeACategory) {
              setSelectedTypeACategory(currentProf.category);
            }
          }

          if (typeof (trader as any).secondary_profession_id !== 'undefined' && (trader as any).secondary_profession_id !== null) {
            setSecondaryProfessionId(Number((trader as any).secondary_profession_id));
          }
        } else if (businessType === 'craftie' && craftie) {
          const { data, error: profError } = await supabase
            .from('craft_professions')
            .select('id, name, category')
            .order('name');

          if (profError) throw profError;

          const options = (data || []) as Array<{ id: number; name: string; category?: string | null }>;
          setProfessionOptions(options);

          const currentId = craftie.craft_professions?.id ?? craftie.profession_id;
          if (currentId) {
            setSelectedProfessionId(Number(currentId));
          }
        } else if (businessType === 'hospitality' && hospitality) {
          const { data, error: catError } = await supabase
            .from('hospitality_categories')
            .select('id, name, category')
            .order('name');

          if (catError) throw catError;

          const options = (data || []) as Array<{ id: number; name: string; category?: string | null }>;
          setProfessionOptions(options);

          const currentId = (hospitality as any).profession_id || hospitality.hospitality_categories?.id;
          if (currentId) {
            setSelectedProfessionId(Number(currentId));
          }
        } else if (businessType === 'professional' && professional) {
          const { data, error: profError } = await supabase
            .from('professionals_categories')
            .select('id, name, category')
            .order('name');

          if (profError) throw profError;

          const options = (data || []) as Array<{ id: number; name: string; category?: string | null }>;
          setProfessionOptions(options);

          const currentId = (professional as any).profession_id;
          if (currentId) {
            setSelectedProfessionId(Number(currentId));
          }
        } else {
          setProfessionOptions([]);
          setSelectedProfessionId(null);
        }
      } catch (err) {
        console.error('Error loading professions/categories for dashboard:', err);
        setProfessionOptions([]);
        setSelectedProfessionId(null);
        setProfessionError('Failed to load available categories/professions');
      } finally {
        setProfessionLoading(false);
      }
    };

    void loadProfessions();
  }, [businessType, trader, craftie, hospitality, professional]);

  // Load feature cards shared across business types
  useEffect(() => {
    const loadFeatures = async () => {
      if (!user || !businessType) {
        return;
      }

      try {
        setFeaturesLoading(true);
        setFeaturesError(null);

        const userId = user.id;
        const existing = await traderService.getTraderFeatures(userId);

        // Derive sensible defaults from the current business details
        let defaultName = '';
        let defaultDescription = '';
        let defaultImage = '/bally-Market-sign-200px.png';

        if (businessType === 'trader' && trader) {
          defaultName = trader.store_name || trader.owner_name;
          defaultDescription = trader.description;
          defaultImage = trader.avatar_url || defaultImage;
        } else if (businessType === 'craftie' && craftie) {
          defaultName = craftie.store_name || craftie.owner_name;
          defaultDescription = craftie.description;
          defaultImage = craftie.avatar_url || defaultImage;
        } else if (businessType === 'hospitality' && hospitality) {
          defaultName = hospitality.store_name || hospitality.owner_name;
          defaultDescription = hospitality.description;
          defaultImage = hospitality.avatar_url || defaultImage;
        } else if (businessType === 'professional' && professional) {
          defaultName = professional.store_name || professional.owner_name;
          defaultDescription = professional.description;
          defaultImage = professional.avatar_url || defaultImage;
        }

        const baseFeatures: TraderFeatures =
          existing || {
            user_id: userId,
            feature1_enabled: true,
            feature1_title: defaultName || 'My Business',
            feature1_description:
              defaultDescription || 'Discover our unique offerings',
            feature1_image: defaultImage,
            feature2_enabled: false,
            feature2_title: 'Orders',
            feature2_description: 'View and manage your orders',
            feature2_image: '/bally-Market-sign-200px.png',
            feature3_enabled: false,
            feature3_title: 'Analytics',
            feature3_description:
              'View your sales and performance analytics',
            feature3_image: '/bally-Market-sign-200px.png',
            features_section_title: 'Featured Offerings',
            features_layout: 'rows',
          };

        setTraderFeatures(baseFeatures);
        setLocalFeatureToggles({
          feature1_enabled: !!baseFeatures.feature1_enabled,
          feature2_enabled: !!baseFeatures.feature2_enabled,
          feature3_enabled: !!baseFeatures.feature3_enabled,
        });
        setFeatureSectionTitle(
          baseFeatures.features_section_title || 'Featured Offerings',
        );
        setFeatureLayout(
          baseFeatures.features_layout === 'columns' ? 'columns' : 'rows',
        );
      } catch (err) {
        console.error('Error loading dashboard features:', err);
        setTraderFeatures(null);
        setLocalFeatureToggles({
          feature1_enabled: false,
          feature2_enabled: false,
          feature3_enabled: false,
        });
        setFeatureSectionTitle('Featured Offerings');
        setFeatureLayout('rows');
        setFeaturesError('Failed to load features');
      } finally {
        setFeaturesLoading(false);
      }
    };

    void loadFeatures();
  }, [user, businessType, trader, craftie, hospitality]);

  useEffect(() => {
    if (!traderFeatures) {
      return;
    }

    setFeatureSectionTitle(
      traderFeatures.features_section_title || 'Featured Offerings',
    );
    setFeatureLayout(
      traderFeatures.features_layout === 'columns' ? 'columns' : 'rows',
    );
  }, [traderFeatures]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6 mt-16">
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
                <Skeleton className="h-6 w-32" />
                <div className="space-y-3">
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
              </div>
              <div className="flex justify-center">
                <Skeleton className="w-full max-w-sm aspect-square rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasBusiness = businessType !== null;

  let name = '';
  let categoryLabel = '';
  let location = '';
  let phone = '';
  let email = '';
  let website = '';
  let avatarUrl = '';
  let description = '';
  let verified = false;
  let mockup = false;
  let publicPath = '';
  let manageLabel = 'Edit details';

  if (businessType === 'trader' && trader) {
    name = trader.store_name || trader.owner_name;
    categoryLabel = trader.profession?.name || 'Trader';
    location = trader.location;
    phone = trader.phone_number;
    email = trader.email;
    website = trader.website || '';
    avatarUrl = trader.avatar_url || '';
    description = trader.description;
    verified = trader.verified;
    mockup = trader.mockup;
    publicPath = `/trader/${trader.slug || trader.id}`;
  } else if (businessType === 'craftie' && craftie) {
    name = craftie.store_name || craftie.owner_name;
    categoryLabel = craftie.craft_professions?.name || 'Craftsperson';
    location = craftie.location;
    phone = craftie.phone_number;
    email = craftie.email;
    website = craftie.website || '';
    avatarUrl = craftie.avatar_url || '';
    description = craftie.description;
    verified = !!craftie.verified;
    mockup = false;
    publicPath = `/craftsman/${craftie.slug || craftie.id}`;
  } else if (businessType === 'hospitality' && hospitality) {
    name = hospitality.store_name || hospitality.owner_name;
    categoryLabel = hospitality.hospitality_categories?.name || 'Hospitality Business';
    location = hospitality.location;
    phone = hospitality.phone_number;
    email = hospitality.email;
    website = hospitality.website || '' as any;
    avatarUrl = hospitality.avatar_url || '';
    description = hospitality.description;
    verified = !!hospitality.verified;
    mockup = !!hospitality.mockup;
    publicPath = `/hospitality/${hospitality.slug || hospitality.id}`;
    manageLabel = 'Edit details';
  } else if (businessType === 'professional' && professional) {
    name = professional.store_name || professional.owner_name;
    categoryLabel = professional.professionals_categories?.name || 'Professional';
    location = professional.location;
    phone = professional.phone_number;
    email = professional.email;
    website = professional.website || '';
    avatarUrl = professional.avatar_url || '';
    description = professional.description;
    verified = !!professional.verified;
    mockup = !!professional.mockup;
    publicPath = `/professional/${professional.slug || professional.id}`;
    manageLabel = 'Edit details';
  }

  // Derive shared map visibility and preview URL based on current business
  const hasLocationForMap = Boolean(location && location.trim().length > 0);

  let showMapFlag = true;
  if (businessType === 'trader' && trader) {
    showMapFlag = (trader as any).show_map !== false;
  } else if (businessType === 'craftie' && craftie) {
    showMapFlag = (craftie as any).show_map !== false;
  } else if (businessType === 'hospitality' && hospitality) {
    showMapFlag = (hospitality as any).show_map !== false;
  } else if (businessType === 'professional' && professional) {
    showMapFlag = (professional as any).show_map !== false;
  }

  const mapUrl = hasLocationForMap && showMapFlag
    ? `https://www.google.com/maps?q=${encodeURIComponent(`${location}, Ireland`)}&output=embed`
    : null;

  // Profession options filtered by selected Type A category for traders
  const filteredProfessionOptions =
    businessType === 'trader' && selectedTypeACategory
      ? professionOptions.filter((opt) => opt.category === selectedTypeACategory)
      : professionOptions;

  const handleEditClick = () => {
    if (!businessType) {
      navigate('/register/trader');
      return;
    }

    if (businessType === 'trader' && trader) {
      setFormState({
        store_name: trader.store_name,
        phone_number: trader.phone_number,
        email: trader.email,
        website: trader.website || '',
        location: trader.location,
        description: trader.description,
      });
    } else if (businessType === 'craftie' && craftie) {
      setFormState({
        store_name: craftie.store_name,
        phone_number: craftie.phone_number,
        email: craftie.email,
        website: craftie.website || '',
        location: craftie.location,
        description: craftie.description,
      });
    } else if (businessType === 'hospitality' && hospitality) {
      setFormState({
        store_name: hospitality.store_name,
        phone_number: hospitality.phone_number,
        email: hospitality.email,
        website: hospitality.website || '',
        location: hospitality.location,
        description: hospitality.description,
      });
    } else if (businessType === 'professional' && professional) {
      setFormState({
        store_name: professional.store_name,
        phone_number: professional.phone_number,
        email: professional.email,
        website: professional.website || '',
        location: professional.location,
        description: professional.description,
      });
    }

    setIsEditing(true);
  };

  const handleFieldChange = (field: keyof BusinessFormState, value: string) => {
    setFormState(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormState(null);
  };

  const handleSaveChanges = async () => {
    if (!businessType || !formState) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (businessType === 'trader' && trader && trader.id) {
        const updatePayload: any = {
          store_name: formState.store_name,
          phone_number: formState.phone_number,
          email: formState.email,
          website: formState.website,
          location: formState.location,
          description: formState.description,
        };

        if (selectedProfessionId) {
          updatePayload.profession_id = selectedProfessionId;
        }

        const updated = await traderService.updateTrader(trader.id, updatePayload);
        if (updated) {
          setTrader(updated);
        }
      } else if (businessType === 'craftie' && craftie && typeof craftie.id !== 'undefined') {
        const craftUpdate: Partial<Craftsman> = {
          store_name: formState.store_name,
          phone_number: formState.phone_number,
          email: formState.email,
          website: formState.website,
          location: formState.location,
          description: formState.description,
        };

        if (selectedProfessionId) {
          (craftUpdate as any).profession_id = selectedProfessionId;
        }

        const updated = await craftsmanService.updateCraftsman(craftie.id as number, craftUpdate);
        if (updated) {
          setCraftie(updated);
        }
      } else if (businessType === 'hospitality' && hospitality) {
        const updateData: any = {
          store_name: formState.store_name,
          phone_number: formState.phone_number,
          email: formState.email,
          website: formState.website,
          location: formState.location,
          description: formState.description,
        };

        if (selectedProfessionId) {
          updateData.profession_id = selectedProfessionId;
        }

        const { data, error: updateError } = await supabase
          .from('hospitality')
          .update(updateData)
          .eq('id', hospitality.id)
          .select('*')
          .single();

        if (updateError) {
          throw updateError;
        }

        if (data) {
          setHospitality(data as HospitalityBusiness);
        }
      } else if (businessType === 'professional' && professional) {
        const updateData: any = {
          store_name: formState.store_name,
          phone_number: formState.phone_number,
          email: formState.email,
          website: formState.website,
          location: formState.location,
          description: formState.description,
        };

        if (selectedProfessionId) {
          updateData.profession_id = selectedProfessionId;
        }

        const { data, error: updateError } = await supabase
          .from('professionals')
          .update(updateData)
          .eq('id', professional.id)
          .select('*')
          .single();

        if (updateError) {
          throw updateError;
        }

        if (data) {
          setProfessional(data as ProfessionalBusiness);
        }
      }

      setIsEditing(false);
      setFormState(null);
    } catch (err) {
      console.error('Error saving business details:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteAccount = async () => {
    if (!user) {
      setShowDeleteDialog(false);
      setRemoveBusinessInfo(false);
      return;
    }

    try {
      setDeletingAccount(true);
      setError(null);

      // Call the delete-account edge function. We pass userId and the
      // removeBusinessInfo flag so the backend can delete the correct
      // account and optionally anonymize linked business data.
      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: { removeBusinessInfo, userId: user.id },
      });

      if (error || (data && (data as any).error)) {
        console.error('Error deleting account via function:', error || (data as any).error);
        setError('Failed to delete account. Please try again.');
        return;
      }

      // After successful deletion, sign out locally and navigate home.
      await supabase.auth.signOut();
      setShowDeleteDialog(false);
      setRemoveBusinessInfo(false);
      navigate('/');
    } catch (deleteErr) {
      console.error('Unexpected error during account deletion:', deleteErr);
      setError('Failed to delete account. Please try again.');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleCancelDeleteAccount = () => {
    setShowDeleteDialog(false);
    setRemoveBusinessInfo(false);
  };

  // Toggle map visibility flag for the current business and persist to Supabase
  const handleToggleMapVisibility = async () => {
    if (!businessType) {
      return;
    }

    try {
      if (businessType === 'trader' && trader && trader.id) {
        const current = (trader as any).show_map ?? true;
        const next = !current;
        const updated = await traderService.updateTrader(trader.id, { show_map: next } as any);
        if (updated) {
          setTrader(updated);
        }
        return;
      }

      if (businessType === 'craftie' && craftie && typeof craftie.id !== 'undefined') {
        const current = (craftie as any).show_map ?? true;
        const next = !current;
        const updated = await craftsmanService.updateCraftsman(craftie.id as number, { show_map: next } as any);
        if (updated) {
          setCraftie(updated);
        }
        return;
      }

      if (businessType === 'hospitality' && hospitality) {
        const current = (hospitality as any).show_map ?? true;
        const next = !current;
        const { data, error: updateError } = await supabase
          .from('hospitality')
          .update({ show_map: next })
          .eq('id', hospitality.id)
          .select('*')
          .single();

        if (updateError) {
          throw updateError;
        }

        if (data) {
          setHospitality(data as HospitalityBusiness);
        }
        return;
      }

      if (businessType === 'professional' && professional) {
        const current = (professional as any).show_map ?? true;
        const next = !current;
        const { data, error: updateError } = await supabase
          .from('professionals')
          .update({ show_map: next })
          .eq('id', professional.id)
          .select('*')
          .single();

        if (updateError) {
          throw updateError;
        }

        if (data) {
          setProfessional(data as ProfessionalBusiness);
        }
      }
    } catch (toggleError) {
      console.error('Error updating map visibility:', toggleError);
      setError('Failed to update map visibility');
    }
  };

  const handleManageClick = () => {
    if (!hasBusiness) {
      navigate('/register/trader');
      return;
    }

    if (isEditing) {
      void handleSaveChanges();
    } else {
      handleEditClick();
    }
  };

  const handleEditFeature = (feature: {
    key: 'feature1' | 'feature2' | 'feature3';
    titleKey: keyof TraderFeatures;
    descKey: keyof TraderFeatures;
    enabledKey: keyof TraderFeatures;
    imageKey: keyof TraderFeatures;
    defaultTitle: string;
    defaultDesc: string;
    defaultImage: string;
  }) => {
    if (!traderFeatures) {
      return;
    }

    setEditingFeature({
      key: feature.key,
      title:
        (traderFeatures[feature.titleKey] as string) || feature.defaultTitle,
      description:
        (traderFeatures[feature.descKey] as string) || feature.defaultDesc,
      image:
        (traderFeatures[feature.imageKey] as string) || feature.defaultImage,
      enabled: !!localFeatureToggles[
        feature.enabledKey as keyof typeof localFeatureToggles
      ],
    });
    setFeatureImageFile(null);
  };

  const handleUpdateFeature = async () => {
    if (!editingFeature || !traderFeatures || !user) {
      return;
    }

    setFeaturesSaving(true);
    setFeaturesError(null);

    try {
      const updateData: Partial<TraderFeatures> = {};

      (updateData as any)[`${editingFeature.key}_title`] =
        editingFeature.title;
      (updateData as any)[`${editingFeature.key}_description`] =
        editingFeature.description;
      (updateData as any)[`${editingFeature.key}_enabled`] =
        editingFeature.enabled;

      if (featureImageFile) {
        const validation = validateImageFile(featureImageFile, 20);
        if (!validation.isValid) {
          setFeaturesError(validation.error ?? 'Please select a smaller image file.');
          return;
        }

        const originalSize = featureImageFile.size;
        const compressedFile = await compressImage(featureImageFile, GALLERY_PRESET);
        const compressedSize = compressedFile.size;
        const reduction =
          originalSize > 0
            ? (((originalSize - compressedSize) / originalSize) * 100).toFixed(0)
            : '0';

        const userId = traderFeatures.user_id || user.id;
        const uploadPath = `trader_features/${userId}/${editingFeature.key}_${Date.now()}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(uploadPath, compressedFile, {
            cacheControl: '3600',
            upsert: true,
          });

        if (uploadError) {
          throw uploadError;
        }

        const { data: urlData } = supabase.storage
          .from('avatars')
          .getPublicUrl(uploadPath);

        if (urlData?.publicUrl) {
          (updateData as any)[`${editingFeature.key}_image`] =
            urlData.publicUrl;

          alert(
            `Feature image optimized: ${formatFileSize(originalSize)} -> ${formatFileSize(
              compressedSize,
            )} (${reduction}% smaller)`,
          );
        }
      }

      const userId = traderFeatures.user_id || user.id;
      const updated = await traderService.updateTraderFeatures(
        userId,
        updateData,
      );

      if (updated) {
        setTraderFeatures(prev => {
          if (!prev) return prev;

          const next: TraderFeatures = {
            ...prev,
            [`${editingFeature.key}_title`]: editingFeature.title,
            [`${editingFeature.key}_description`]: editingFeature.description,
            [`${editingFeature.key}_enabled`]: editingFeature.enabled,
            updated_at: new Date().toISOString(),
          } as any;

          if ((updateData as any)[`${editingFeature.key}_image`]) {
            (next as any)[`${editingFeature.key}_image`] = (
              updateData as any
            )[`${editingFeature.key}_image`];
          }

          return next;
        });

        setLocalFeatureToggles(prev => ({
          ...prev,
          [`${editingFeature.key}_enabled`]: editingFeature.enabled,
        }) as any);

        setEditingFeature(null);
        setFeatureImageFile(null);
      } else {
        setFeaturesError('Failed to update feature');
      }
    } catch (err) {
      console.error('Error updating feature:', err);
      setFeaturesError('Failed to update feature');
    } finally {
      setFeaturesSaving(false);
    }
  };

  const updateFeatureStatus = async (
    featureKey: 'feature1' | 'feature2' | 'feature3',
    newStatus: boolean,
  ) => {
    if (!traderFeatures || !user) {
      return;
    }

    setLocalFeatureToggles(prev => ({
      ...prev,
      [`${featureKey}_enabled`]: newStatus,
    }) as any);

    try {
      const updateData: Partial<TraderFeatures> = {};
      (updateData as any)[`${featureKey}_enabled`] = newStatus;

      const userId = traderFeatures.user_id || user.id;
      const updated = await traderService.updateTraderFeatures(
        userId,
        updateData,
      );

      if (updated) {
        setTraderFeatures(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            [`${featureKey}_enabled`]: newStatus,
            updated_at: new Date().toISOString(),
          } as any;
        });
      } else {
        setLocalFeatureToggles(prev => ({
          ...prev,
          [`${featureKey}_enabled`]: !newStatus,
        }) as any);
        setFeaturesError('Failed to update feature status');
      }
    } catch (err) {
      console.error('Error updating feature status:', err);
      setLocalFeatureToggles(prev => ({
        ...prev,
        [`${featureKey}_enabled`]: !newStatus,
      }) as any);
      setFeaturesError('Failed to update feature status');
    }
  };

  const renderFeaturesSection = () => {
    if (!user || !businessType) {
      return null;
    }

    if (featuresLoading && !traderFeatures) {
      return (
        <p className="text-sm text-gray-500">
          Loading features...
        </p>
      );
    }

    if (!traderFeatures) {
      return null;
    }

    const featuresToRender: Array<{
      key: 'feature1' | 'feature2' | 'feature3';
      label: string;
      enabledKey: keyof TraderFeatures;
      titleKey: keyof TraderFeatures;
      descKey: keyof TraderFeatures;
      imageKey: keyof TraderFeatures;
      defaultTitle: string;
      defaultDesc: string;
      defaultImage: string;
    }> = [
      {
        key: 'feature1',
        label: 'Feature 1',
        enabledKey: 'feature1_enabled',
        titleKey: 'feature1_title',
        descKey: 'feature1_description',
        imageKey: 'feature1_image',
        defaultTitle:
          (businessType === 'trader' && trader?.store_name) ||
          (businessType === 'craftie' && craftie?.store_name) ||
          (businessType === 'hospitality' &&
            hospitality?.store_name) ||
          (businessType === 'professional' && professional?.store_name) ||
          'My Business',
        defaultDesc:
          (businessType === 'trader' && trader?.description) ||
          (businessType === 'craftie' && craftie?.description) ||
          (businessType === 'hospitality' &&
            hospitality?.description) ||
          (businessType === 'professional' && professional?.description) ||
          'Discover our unique offerings',
        defaultImage:
          (businessType === 'trader' && trader?.avatar_url) ||
          (businessType === 'craftie' && craftie?.avatar_url) ||
          (businessType === 'hospitality' &&
            hospitality?.avatar_url) ||
          (businessType === 'professional' && professional?.avatar_url) ||
          '/bally-Market-sign-200px.png',
      },
      {
        key: 'feature2',
        label: 'Feature 2',
        enabledKey: 'feature2_enabled',
        titleKey: 'feature2_title',
        descKey: 'feature2_description',
        imageKey: 'feature2_image',
        defaultTitle: 'Orders',
        defaultDesc: 'View and manage your orders',
        defaultImage: '/bally-Market-sign-200px.png',
      },
      {
        key: 'feature3',
        label: 'Feature 3',
        enabledKey: 'feature3_enabled',
        titleKey: 'feature3_title',
        descKey: 'feature3_description',
        imageKey: 'feature3_image',
        defaultTitle: 'Analytics',
        defaultDesc: 'View your sales and performance analytics',
        defaultImage: '/bally-Market-sign-200px.png',
      },
    ];

    const previewFeatures = featuresToRender
      .filter(feature => {
        const enabled = traderFeatures[feature.enabledKey];
        return Boolean(enabled);
      })
      .map(feature => {
        const title =
          (traderFeatures[feature.titleKey as keyof TraderFeatures] as string) ||
          feature.defaultTitle;
        const description =
          (traderFeatures[feature.descKey as keyof TraderFeatures] as string) ||
          feature.defaultDesc;
        const imageUrl =
          (traderFeatures[feature.imageKey as keyof TraderFeatures] as string) ||
          feature.defaultImage;

        return {
          title,
          description,
          image: imageUrl,
        };
      });

    const handleSaveFeatureSectionTitle = async () => {
      if (!traderFeatures) {
        return;
      }

      try {
        setFeatureMetaSaving(true);
        setFeatureMetaError(null);

        const updated = await traderService.updateTraderFeatures(
          traderFeatures.user_id,
          {
            features_section_title: featureSectionTitle,
          },
        );

        if (updated) {
          setTraderFeatures(prev =>
            prev
              ? {
                  ...prev,
                  features_section_title: updated.features_section_title,
                }
              : prev,
          );
          setFeatureSectionTitle(
            updated.features_section_title || 'Featured Offerings',
          );
        }
      } catch (error) {
        console.error('Error updating feature section title:', error);
        setFeatureMetaError('Failed to update feature section title.');
      } finally {
        setFeatureMetaSaving(false);
      }
    };

    const handleFeatureLayoutChange = async (nextLayout: FeatureShowcaseLayout) => {
      if (!traderFeatures || featureLayout === nextLayout) {
        return;
      }

      try {
        setFeatureMetaSaving(true);
        setFeatureMetaError(null);

        const updated = await traderService.updateTraderFeatures(
          traderFeatures.user_id,
          {
            features_layout: nextLayout,
          },
        );

        if (updated) {
          setTraderFeatures(prev =>
            prev
              ? {
                  ...prev,
                  features_layout: updated.features_layout,
                }
              : prev,
          );
          setFeatureLayout(
            updated.features_layout === 'columns' ? 'columns' : 'rows',
          );
        }
      } catch (error) {
        console.error('Error updating feature layout:', error);
        setFeatureMetaError('Failed to update feature layout.');
      } finally {
        setFeatureMetaSaving(false);
      }
    };

    return (
      <>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Features section title
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={featureSectionTitle}
                onChange={e => setFeatureSectionTitle(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Featured Offerings"
              />
              <button
                type="button"
                onClick={handleSaveFeatureSectionTitle}
                disabled={featureMetaSaving || !featureSectionTitle.trim()}
                className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {featureMetaSaving ? 'Saving...' : 'Save title'}
              </button>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Desktop layout</p>
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
              {(['rows', 'columns'] as FeatureShowcaseLayout[]).map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleFeatureLayoutChange(option)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    featureLayout === option
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } ${option === 'rows' ? 'border-r border-gray-200' : ''}`}
                >
                  {option === 'rows' ? 'Rows (default)' : 'Columns'}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Rows alternate image and description in two columns. Columns show each feature as a separate card.
            </p>
          </div>

          {featureMetaError && (
            <p className="text-sm text-red-500">{featureMetaError}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuresToRender.map(feature => {
            const isEditing = editingFeature?.key === feature.key;
            const isEnabled = !!localFeatureToggles[
              feature.enabledKey as keyof typeof localFeatureToggles
            ];
            const title =
              (traderFeatures[
                feature.titleKey as keyof TraderFeatures
              ] as string) || feature.defaultTitle;
            const description =
              (traderFeatures[
                feature.descKey as keyof TraderFeatures
              ] as string) || feature.defaultDesc;
            const imageUrl =
              (traderFeatures[
                feature.imageKey as keyof TraderFeatures
              ] as string) || feature.defaultImage;

            return (
              <div
                key={feature.key}
                className={`bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative ${
                  !isEnabled ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-gray-600">
                    {feature.label}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => updateFeatureStatus(feature.key, !isEnabled)}
                      className={`relative inline-flex h-5 w-10 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                        isEnabled ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                      aria-pressed={isEnabled}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                          isEnabled ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEditFeature(feature)}
                      className="text-gray-500 hover:text-blue-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {isEditing && editingFeature ? (
                  <div className="space-y-4">
                    <div className="flex justify-center mb-4">
                      <img
                        src={
                          featureImageFile
                            ? URL.createObjectURL(featureImageFile)
                            : editingFeature.image || feature.defaultImage
                        }
                        alt={`${editingFeature.title} Icon`}
                        className="h-32 w-32 object-contain rounded-lg"
                        onError={e => {
                          const imgEl = e.target as HTMLImageElement;
                          imgEl.src = feature.defaultImage;
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Feature Title
                      </label>
                      <input
                        type="text"
                        value={editingFeature.title}
                        onChange={e =>
                          setEditingFeature(prev =>
                            prev
                              ? {
                                  ...prev,
                                  title: e.target.value,
                                }
                              : null,
                          )
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Feature Description
                      </label>
                      <textarea
                        value={editingFeature.description}
                        onChange={e =>
                          setEditingFeature(prev =>
                            prev
                              ? {
                                  ...prev,
                                  description: e.target.value,
                                }
                              : null,
                          )
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Feature Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              alert(
                                'File is too large. Maximum size is 5MB.',
                              );
                              e.target.value = '';
                              return;
                            }
                            setFeatureImageFile(file);
                          }
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                    </div>
                    <div className="flex justify-between mt-4">
                      <button
                        type="button"
                        onClick={handleUpdateFeature}
                        disabled={featuresSaving}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {featuresSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingFeature(null);
                          setFeatureImageFile(null);
                          setFeaturesError(null);
                        }}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-start mb-4">
                      <img
                        src={imageUrl}
                        alt={`${title} Icon`}
                        className="h-16 w-16 object-contain"
                        onError={e => {
                          const imgEl = e.target as HTMLImageElement;
                          imgEl.src = feature.defaultImage;
                        }}
                      />
                    </div>
                    <h3 className="font-semibold mb-2 text-left text-gray-900">
                      {title}
                    </h3>
                    <p className="text-gray-600 text-left text-sm">
                      {description}
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Public preview
          </h3>
          <FeatureShowcase
            title={featureSectionTitle}
            features={previewFeatures}
            layout={featureLayout}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4"
          />
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex-shrink-0">
            <Link to="/">
              <img
                src="/bally-Market-sign-200px.png"
                alt="Ballyconnell Market Logo"
                className="h-12 w-auto cursor-pointer"
              />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <h1 className="text-2xl font-semibold text-gray-900 mb-1">
                Dashboard
              </h1>
              <p className="text-sm text-gray-500">
                Welcome{' '}
                {
                  (businessType === 'trader' && trader?.owner_name) ||
                  (businessType === 'craftie' && craftie?.owner_name) ||
                  (businessType === 'hospitality' && hospitality?.owner_name) ||
                  user?.email
                }
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowDeleteDialog(true)}
              className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Delete account
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!hasBusiness ? (
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
            {/* Show claimable business if found matching user's email */}
            {claimableBusiness ? (
              <>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    We Found Your Business!
                  </h2>
                  <p className="text-gray-600">
                    A business listing matching your email address was found. Claim it to start managing your profile.
                  </p>
                </div>

                {/* Claimable Business Card */}
                <div className="border-2 border-green-200 bg-green-50 rounded-lg p-6">
                  <div className="flex items-center gap-4">
                    {/* Business Avatar */}
                    <div className="flex-shrink-0">
                      {claimableBusiness.avatar_url ? (
                        <img
                          src={claimableBusiness.avatar_url}
                          alt={claimableBusiness.store_name}
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center">
                          <Building2 className="w-10 h-10 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Business Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {claimableBusiness.store_name}
                      </h3>
                      <p className="text-sm text-gray-600 flex items-center mt-1">
                        <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                        {claimableBusiness.location}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 capitalize">
                        {claimableBusiness.table === 'crafties' ? 'Craftsperson' : claimableBusiness.table.slice(0, -1)}
                      </p>
                    </div>

                    {/* Claim Button */}
                    <div className="flex-shrink-0">
                      <button
                        onClick={handleClaimBusiness}
                        disabled={claimingBusiness}
                        className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {claimingBusiness ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Claiming...
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-5 h-5 mr-2" />
                            Claim This Business
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="text-center text-sm text-gray-500">
                  <p>Not your business? You can also:</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link
                    to="/register/business"
                    className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
                  >
                    Register a New Business
                  </Link>
                  <Link
                    to="/search"
                    className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
                  >
                    Search for Another Business
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Get Started
                  </h2>
                  <p className="text-gray-600">
                    You have not connected a business to your account yet. Choose an option below to get started.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Option 1: Register New Business */}
                  <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      Register a New Business
                    </h3>
                    <p className="text-gray-500 mb-4 text-sm">
                      Create a new profile for your business.
                    </p>
                    <Link
                      to="/register/business"
                      className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                    >
                      Register Business
                    </Link>
                  </div>

                  {/* Option 2: Claim Existing Business */}
                  <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      Claim Existing Business
                    </h3>
                    <p className="text-gray-500 mb-4 text-sm">
                      Is your business already listed on BallyMarket? Search and claim it to manage your profile.
                    </p>
                    <Link
                      to="/search"
                      className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
                    >
                      Find & Claim Business
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Details */}
                <div>
                  <div className="flex items-center mb-4">
                    {isEditing && formState ? (
                      <input
                        type="text"
                        value={formState.store_name}
                        onChange={(e) => handleFieldChange('store_name', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-2xl font-bold text-gray-900"
                      />
                    ) : (
                      <h2 className="text-3xl font-bold text-gray-900">
                        {name}
                      </h2>
                    )}
                    {verified && (
                      <CheckCircle className="ml-3 w-6 h-6 text-blue-500" />
                    )}
                    {mockup && (
                      <span className="ml-3 bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        Demo
                      </span>
                    )}
                  </div>

                  <p className="text-lg text-gray-600 mb-6">
                    {categoryLabel}
                  </p>

                  {isEditing && formState ? (
                    <div className="space-y-4">
                      {professionOptions.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {businessType === 'hospitality' ? 'Hospitality category' : 'Profession / category'}
                          </label>
                          <select
                            value={selectedProfessionId ?? ''}
                            onChange={(e) =>
                              setSelectedProfessionId(
                                e.target.value ? Number(e.target.value) : null
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          >
                            <option value="">
                              Select...
                            </option>
                            {professionOptions.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.name}
                              </option>
                            ))}
                          </select>
                          {professionLoading && (
                            <p className="mt-1 text-xs text-gray-500">
                              Loading options...
                            </p>
                          )}
                          {professionError && (
                            <p className="mt-1 text-sm text-red-500">
                              {professionError}
                            </p>
                          )}
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="text"
                          value={formState.phone_number}
                          onChange={(e) => handleFieldChange('phone_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={formState.email}
                          onChange={(e) => handleFieldChange('email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Website
                        </label>
                        <input
                          type="text"
                          value={formState.website}
                          onChange={(e) => handleFieldChange('website', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location
                        </label>
                        <input
                          type="text"
                          value={formState.location}
                          onChange={(e) => handleFieldChange('location', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {phone && (
                        <p className="flex items-center text-gray-700">
                          <Phone className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium mr-2">Phone:</span>
                          <a
                            href={`tel:${phone}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {phone}
                          </a>
                        </p>
                      )}

                      {email && (
                        <p className="flex items-center text-gray-700">
                          <Mail className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium mr-2">Email:</span>
                          <a
                            href={`mailto:${email}`}
                            className="text-blue-600 hover:text-blue-800 truncate"
                          >
                            {email}
                          </a>
                        </p>
                      )}

                      {website && (
                        <p className="flex items-center text-gray-700">
                          <Globe className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium mr-2">Website:</span>
                          <a
                            href={website.startsWith('http') ? website : `https://${website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 truncate"
                          >
                            {website}
                          </a>
                        </p>
                      )}

                      {location && (
                        <p className="flex items-center text-gray-700">
                          <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium mr-2">Location:</span>
                          {location}
                        </p>
                      )}

                      {hasLocationForMap && (
                        <div className="pt-2 border-t border-gray-100">
                          <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                            <span>Show map on your public page</span>
                            <button
                              type="button"
                              onClick={handleToggleMapVisibility}
                              className={`px-3 py-1 rounded-full border text-xs font-medium ${
                                showMapFlag
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : 'bg-gray-50 text-gray-500 border-gray-200'
                              }`}
                            >
                              {showMapFlag ? 'On' : 'Off'}
                            </button>
                          </div>
                          {mapUrl && (
                            <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                              <iframe
                                title="Location map preview"
                                src={mapUrl}
                                width="100%"
                                height="220"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                              />
                            </div>
                          )}
                          {!mapUrl && (
                            <p className="mt-2 text-xs text-gray-500">
                              Map is hidden on your public page because visibility is turned off above.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Column - Image */}
                <div className="flex justify-center items-start">
                  <div className="relative w-full max-w-sm aspect-square rounded-lg overflow-hidden bg-gray-100">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <img
                          src={`https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
                            name || (user?.email || '')
                          )}&backgroundColor=f3f4f6&fontSize=40`}
                          alt={name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={avatarUploading}
                      className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-600 shadow-md ring-1 ring-black/5 hover:bg-gray-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Change avatar"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (event) => {
                      const file = event.target.files?.[0];
                      if (!file || !user || !businessType) return;

                      setAvatarUploading(true);
                      setError(null);

                      try {
                        const originalSize = file.size;
                        const compressedFile = await compressImage(file, AVATAR_PRESET);
                        const compressedSize = compressedFile.size;
                        const reduction =
                          originalSize > 0
                            ? (((originalSize - compressedSize) / originalSize) * 100).toFixed(0)
                            : '0';

                        if (businessType === 'trader' && trader && trader.id) {
                          const url = await traderService.uploadAvatar(trader.id, compressedFile);
                          const updated = await traderService.updateTrader(trader.id, {
                            avatar_url: url,
                          } as any);
                          if (updated) {
                            setTrader(updated);
                            alert(
                              `Avatar optimized: ${formatFileSize(originalSize)} → ${formatFileSize(
                                compressedSize
                              )} (${reduction}% smaller)`
                            );
                          }
                        } else if (
                          businessType === 'craftie' &&
                          craftie &&
                          typeof craftie.id !== 'undefined'
                        ) {
                          const url = await craftsmanService.uploadAvatar(
                            craftie.id as number,
                            compressedFile
                          );
                          const updated = await craftsmanService.updateCraftsman(
                            craftie.id as number,
                            {
                              avatar_url: url,
                            }
                          );
                          if (updated) {
                            setCraftie(updated);
                            alert(
                              `Avatar optimized: ${formatFileSize(originalSize)} → ${formatFileSize(
                                compressedSize
                              )} (${reduction}% smaller)`
                            );
                          }
                        } else if (businessType === 'hospitality' && hospitality) {
                          const url = await traderService.uploadAvatar(
                            hospitality.id,
                            compressedFile
                          );
                          const { data, error: updateError } = await supabase
                            .from('hospitality')
                            .update({ avatar_url: url })
                            .eq('id', hospitality.id)
                            .select('*')
                            .single();

                          if (updateError) {
                            throw updateError;
                          }

                          if (data) {
                            setHospitality(data as HospitalityBusiness);
                            alert(
                              `Avatar optimized: ${formatFileSize(originalSize)} → ${formatFileSize(
                                compressedSize
                              )} (${reduction}% smaller)`
                            );
                          }
                        } else if (businessType === 'professional' && professional) {
                          // Upload avatar for professional business
                          const uploadPath = `professionals/${professional.id}_${Date.now()}.jpg`;
                          
                          const { error: uploadError } = await supabase.storage
                            .from('avatars')
                            .upload(uploadPath, compressedFile, {
                              cacheControl: '3600',
                              upsert: true,
                            });

                          if (uploadError) {
                            throw uploadError;
                          }

                          const { data: urlData } = supabase.storage
                            .from('avatars')
                            .getPublicUrl(uploadPath);

                          const url = urlData?.publicUrl;

                          // Update professional record with new avatar URL
                          const { data, error: updateError } = await supabase
                            .from('professionals')
                            .update({ avatar_url: url })
                            .eq('id', professional.id)
                            .select('*')
                            .single();

                          if (updateError) {
                            throw updateError;
                          }

                          if (data) {
                            setProfessional(data as ProfessionalBusiness);
                            alert(
                              `Avatar optimized: ${formatFileSize(originalSize)} → ${formatFileSize(
                                compressedSize
                              )} (${reduction}% smaller)`
                            );
                          }
                        }
                      } catch (avatarError) {
                        console.error('Error updating avatar:', avatarError);
                        setError('Failed to update avatar');
                      } finally {
                        setAvatarUploading(false);
                        if (event.target) {
                          event.target.value = '';
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* About Section */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
                {isEditing && formState ? (
                  <textarea
                    value={formState.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px]"
                  />
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {description || 'No description available.'}
                  </p>
                )}
              </div>

              {/* Action buttons under details and About section */}
              <div className="mt-6 flex flex-wrap gap-3">
                {publicPath && (
                  <a
                    href={publicPath}
                    className="inline-flex items-center px-4 py-2 rounded-md border border-blue-600 text-blue-600 text-sm font-medium hover:bg-blue-50"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View public page
                  </a>
                )}
                <button
                  onClick={handleManageClick}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : isEditing ? 'Save changes' : manageLabel}
                </button>
                {isEditing && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                )}
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <SocialLinksCard />
              </div>

              {/* Features Section */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Features
                </h2>
                {featuresError && (
                  <p className="mb-3 text-sm text-red-500">
                    {featuresError}
                  </p>
                )}
                {renderFeaturesSection()}
              </div>

              {/* Advanced Section - Trader Type A and categories */}
              {businessType === 'trader' && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Advanced
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Fine-tune your primary trade category and profession, and
                    optionally add a second profession so you can appear in
                    more than one trade list.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Category (Type A) selector */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category (Type A)
                      </label>
                      <select
                        value={selectedTypeACategory}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedTypeACategory(value);
                          // Reset selections when Type A changes so the user
                          // explicitly chooses new primary/secondary trades.
                          setSelectedProfessionId(null);
                          setSecondaryProfessionId(null);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="">
                          All Type A categories
                        </option>
                        {TRADER_TYPE_A_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        This controls which professions are shown below and is
                        pre-filled from your current trade.
                      </p>
                    </div>

                    {/* Primary profession */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Primary profession
                      </label>
                      <select
                        value={selectedProfessionId ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value) {
                            setSelectedProfessionId(null);
                            return;
                          }
                          const id = Number(value);
                          setSelectedProfessionId(id);
                          const prof = professionOptions.find((opt) => opt.id === id);
                          if (prof?.category) {
                            setSelectedTypeACategory(prof.category);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="">
                          Select primary profession...
                        </option>
                        {filteredProfessionOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))}
                      </select>
                      {professionLoading && (
                        <p className="mt-1 text-xs text-gray-500">
                          Loading professions...
                        </p>
                      )}
                      {professionError && (
                        <p className="mt-1 text-sm text-red-500">
                          {professionError}
                        </p>
                      )}
                    </div>

                    {/* Optional second profession */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Second profession (optional)
                      </label>
                      <select
                        value={secondaryProfessionId ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value) {
                            setSecondaryProfessionId(null);
                            return;
                          }
                          const id = Number(value);
                          setSecondaryProfessionId(id);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="">
                          None
                        </option>
                        {filteredProfessionOptions
                          .filter((opt) => opt.id !== selectedProfessionId)
                          .map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      disabled={!selectedProfessionId}
                      onClick={async () => {
                        if (businessType !== 'trader' || !trader || !trader.id || !selectedProfessionId) {
                          return;
                        }

                        try {
                          const updatePayload: any = {
                            profession_id: selectedProfessionId,
                            secondary_profession_id:
                              secondaryProfessionId ?? null,
                          };

                          const updated = await traderService.updateTrader(
                            trader.id,
                            updatePayload,
                          );
                          if (updated) {
                            setTrader(updated);
                          }
                        } catch (err) {
                          console.error('Error updating advanced categories:', err);
                          setError('Failed to update advanced categories');
                        }
                      }}
                      className="inline-flex items-center px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save trade categories
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delete Account Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Delete account</h2>
              <p className="text-sm text-gray-700 mb-4">
                Are you sure? This will delete your user account from the database.
              </p>
              <label className="flex items-center gap-2 mb-6 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  checked={removeBusinessInfo}
                  onChange={(e) => setRemoveBusinessInfo(e.target.checked)}
                />
                <span>Also remove my business information</span>
              </label>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancelDeleteAccount}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDeleteAccount}
                  disabled={deletingAccount}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingAccount ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
